import "@assets/css/bootstrap-grid.css";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { FormSkeleton, SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import { showToast, throwErrorAlert } from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { FormField } from "@utils/interface/form.interface";
import {
  homeCherishMomentSchema,
  HomeCherishMomentFormTypes,
} from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { HomeCherishMomentData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

const HomeCherishMomentForm: React.FC = () => {
  const pathname = usePathname();
  const { searchParams, handleAction } = useHandleAction({});
  const queryClient = useQueryClient();
  const { setIsFormPending } = useSelectionStore();

  const pageItem = searchParams.get("page-item");
  const pageMode = searchParams.get("page");

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: zodResolver(homeCherishMomentSchema),
  });

  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<HomeCherishMomentData>
  >({
    endpoint: endpoint["home-cherish-moments"],
    id: pageItem || "",
    key: "home-cherish-moment",
    params: {
      id: pageItem || "",
    },
    enabled: pageItem ? true : false,
  });

  const handleMutate = (data: any) => {
    const payload: any = {
      title: data.title,
      sub_text: data.sub_text,
    };

    if (data.image_link) {
      payload.image_link = data.image_link;
    }

    if (pageItem) {
      payload.id = pageItem;
    }

    return {
      data: payload,
      endpoint: endpoint["home-cherish-moments"],
      method: pageItem ? METHODS.PUT : METHODS.POST,
    };
  };

  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    setIsFormPending(false);
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });

      handleAction({ type: "page-close" });
      showToast({
        description: `${pathname} ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };

  const handleError = async (error: any) => {
    setIsFormPending(false);
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    const errorMessage = error.data.response.data;
    throwErrorAlert(errorMessage);
  };

  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(pageItem ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;

      if (pageMode === "add") {
        return reset({});
      } else if (firstResult) {
        reset(() => ({
          title: firstResult?.title || "",
          sub_text: firstResult?.sub_text || "",
          image_link: firstResult?.image_link || null,
        }));
      }
    }
  }, [fetchData?.data, fetchPending]);

  const { mutate } = usePostData();

  const onSubmit: SubmitHandler<HomeCherishMomentFormTypes> = (data) => {
    setIsFormPending(true);
    mutate(handleMutate(data), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema: FormField[] = useMemo(
    () => [
      {
        name: "title",
        label: "Title",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Title",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "sub_text",
        label: "Sub Text",
        colSpan: 6,
        type: "text",
        placeholder: "Enter Sub Text",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "image_link",
        label: "Image",
        colSpan: 6,
        type: "cropper",
        returnFile: false,
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
    ],
    [control, errors, setValue]
  );

  if (fetchPending && searchParams.get("modal-item"))
    return (
      <>
        <SkeletonTwo />
      </>
    );

  if (fetchPending && pageItem)
    return (
      <>
        <FormSkeleton />
      </>
    );

  return (
    <>
      <form
        id={pathname + "cherish-moment"}
        onSubmit={handleSubmit(onSubmit)}
        className="row w-full"
      >
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default HomeCherishMomentForm;
